import {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Button,
    Card,
    Col,
    Input,
    Row,
    Select,
    Space,
    Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { IOrderSearch } from '../../../../interfaces/requests.interface';
import {
    BatteryLevel,
    getBatteryLevel,
    ListedStatus,
} from './utils/orderSearch.utils';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

type OrderSearchFiltersProps = {
    searchDetails: IOrderSearch[];
    total: number;
    onFilteredChange: (filtered: IOrderSearch[]) => void;
};

const OrderSearchFilters: FC<OrderSearchFiltersProps> = ({
    searchDetails,
    total,
    onFilteredChange,
}) => {
    const { t } = useTranslation('small-fleet-orders');

    const [listedFilter, setListedFilter] = useState<ListedStatus>('all');
    const [roundFilter, setRoundFilter] = useState<number[]>([1, 2, 3]);
    const [searchText, setSearchText] = useState('');
    const [batteryFilter, setBatteryFilter] = useState<BatteryLevel>('all');
    const [visibilityFilter, setVisibilityFilter] = useState<ListedStatus[]>(['listed']);

    const filteredSearchDetails = useMemo(() => {
        let filtered = [...searchDetails];

        // Filtro de visibilidad (listados / no listados)
        if (!visibilityFilter.includes('not-listed')) {
            filtered = filtered.filter((item) => item.metadata.wasListed);
        }

        // Filtro por wasListed
        if (listedFilter !== 'all' && visibilityFilter.includes('not-listed')) {
            filtered = filtered.filter((item) => {
                const isListed = item.metadata.wasListed;
                return listedFilter === 'listed' ? isListed : !isListed;
            });
        }

        // Filtro por ronda
        if (roundFilter.length > 0) {
            filtered = filtered.filter((item) => roundFilter.includes(item.round));
        }

        // Filtro por nombre o email
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase().trim();
            filtered = filtered.filter(
                (item) => item.driverName.toLowerCase().includes(searchLower)
                    || item.driverEmail.toLowerCase().includes(searchLower),
            );
        }

        // Filtro por batería
        if (batteryFilter !== 'all') {
            filtered = filtered.filter((item) => {
                const level = getBatteryLevel(item.metadata.batteryPercentage);
                return level === batteryFilter;
            });
        }

        // Ordenar: primero listados, luego no listados
        filtered.sort((a, b) => {
            if (a.metadata.wasListed === b.metadata.wasListed) return 0;
            return a.metadata.wasListed ? -1 : 1;
        });

        return filtered;
    }, [searchDetails, listedFilter, roundFilter, searchText, batteryFilter, visibilityFilter]);

    useEffect(() => {
        onFilteredChange(filteredSearchDetails);
    }, [filteredSearchDetails, onFilteredChange]);

    const shouldShowClear = (
        listedFilter !== 'all'
        || roundFilter.length < 3
        || searchText
        || batteryFilter !== 'all'
        || visibilityFilter.length !== 1
        || visibilityFilter[0] !== 'listed'
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row justify="space-between" align="middle">
                <Col>
                    <Text strong>
                        {t('SHOWING_RESULTS', {
                            count: filteredSearchDetails.length,
                            total,
                        })}
                    </Text>
                </Col>
                <Col>
                    {shouldShowClear && (
                        <Button
                            type="link"
                            onClick={() => {
                                setListedFilter('all');
                                setRoundFilter([1, 2, 3]);
                                setSearchText('');
                                setBatteryFilter('all');
                                setVisibilityFilter(['listed']);
                            }}
                        >
                            {t('CLEAR_FILTERS')}
                        </Button>
                    )}
                </Col>
            </Row>
            <Card size="small" style={{ backgroundColor: 'var(--color-gray-0)' }}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Search
                        placeholder={t('SEARCH_DRIVER_NAME_EMAIL')}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                    <Row gutter={[8, 8]}>
                        <Col flex="1 1 0">
                            <Select
                                mode="multiple"
                                value={roundFilter}
                                onChange={setRoundFilter}
                                placeholder={t('SELECT_ROUNDS')}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                            >
                                <Option value={1}>{t('ROUND_1')}</Option>
                                <Option value={2}>{t('ROUND_2')}</Option>
                                <Option value={3}>{t('ROUND_3')}</Option>
                            </Select>
                        </Col>
                        <Col flex="1 1 0">
                            <Select
                                mode="multiple"
                                value={visibilityFilter}
                                onChange={(value) => setVisibilityFilter(value as ListedStatus[])}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                            >
                                <Option value="listed">{t('LISTED_DRIVERS')}</Option>
                                <Option value="not-listed">{t('NOT_LISTED_DRIVERS')}</Option>
                            </Select>
                        </Col>
                    </Row>
                </Space>
            </Card>
        </Space>
    );
};

export { OrderSearchFilters };
